import { useMemo } from 'react';
import {
  OrgAnalyticsDimensionType,
  OrgAnalyticsLabelValueFilterInput,
  OrgAnalyticsContext,
  useOrganizationAnalyticsQuery,
  OrganizationAnalyticsQuery
} from 'graphql/types';
import { hasValidStartDate } from 'modules/Insights/utils';
import { useSupervisorInsightContext } from '../SupervisorInsightContextProvider';
import { useGetPreviousPeriod } from './useGetPreviousPeriod';
import { encodeDataId } from './useGetSupervisorAnalyticsData';

export const useGetPreviousSupervisorData = (
  dimension: OrgAnalyticsDimensionType,
  labels: OrgAnalyticsLabelValueFilterInput[],
  context: OrgAnalyticsContext
): {
  data: OrganizationAnalyticsQuery | undefined;
  loading: boolean;
  isEmpty: boolean;
  encodedId: string;
  prevStartDate: string;
} => {
  const {
    currentPeriod,
    selectedViewOptionSupervisor
  } = useSupervisorInsightContext();
  const { prevStartDate, prevEndDate } = useGetPreviousPeriod({
    currentPeriod: currentPeriod,
    selectedViewOption: selectedViewOptionSupervisor
  });
  const encodeId = encodeDataId({
    dimensionValue: dimension,
    contextValue: context,
    labels: labels,
    dateRangeValue: {
      startDate: prevStartDate || '',
      endDate: prevEndDate || ''
    }
  });
  const isStartTimeValid = hasValidStartDate(prevStartDate || '');
  const { data, loading } = useOrganizationAnalyticsQuery({
    variables: {
      id: encodeId
    },
    skip: !isStartTimeValid
  });
  return useMemo(
    () => ({
      data: data,
      loading: loading,
      isEmpty: Boolean(data?.orgAnalytics?.analyticsResult.items.length === 0),
      encodedId: encodeId,
      prevStartDate: prevStartDate
    }),
    [data, encodeId, loading, prevStartDate]
  );
};
